/**
 * How the item pickers read what was typed into the SKU and Product name
 * boxes. The rules mirror the server's own duplicate checks (the API's
 * sku-code.ts and sku-name.ts), so whatever two items the server would call
 * the same, these call the same too.
 */

/** Letters and digits only, upper-cased: "f-52-01 " → "F5201". */
function squash(text: string): string {
  return text.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function digits(code: string): string {
  return code.replace(/\D/g, '').replace(/^0+/, '');
}

function letters(code: string): string {
  return code.toUpperCase().replace(/[^A-Z]/g, '');
}

/**
 * The same item number: case, separators and leading zeros ignored, and the
 * letters agreeing or left off — so "8", "sku8" and "SKU-00008" are one code,
 * while "DB17357983" and "DN17357983" stay two.
 */
export function sameSkuCode(code: string, typed: string): boolean {
  const a = squash(code);
  const b = squash(typed);
  if (a === '' || b === '') return false;
  if (a === b) return true;
  const number = digits(a);
  if (number === '' || number !== digits(b)) return false;
  const la = letters(a);
  const lb = letters(b);
  return la === lb || la === '' || lb === '';
}

/** Whether the SKU box should list `code` for what was typed: any part of
 *  the code, ignoring case and separators, or the same item number. */
export function skuCodeMatches(code: string, typed: string): boolean {
  const q = squash(typed);
  return q === '' || squash(code).includes(q) || sameSkuCode(code, typed);
}

/** A name without case, spacing or punctuation, in any script — the form two
 *  items may not share: "Layer Mash 50 kg" and "layer-mash 50KG" are one. */
export function skuNameKey(name: string): string {
  return name
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]/gu, '');
}

export function sameSkuName(name: string, typed: string): boolean {
  const key = skuNameKey(typed);
  return key !== '' && key === skuNameKey(name);
}

/** Whether the Product name box should list an item: every typed word
 *  appears in its English or Urdu name, in any order, spacing and
 *  punctuation aside — "50kg" finds "50 kg", "grower broiler" finds
 *  "Broiler Grower". */
export function skuNameMatches(names: { nameEn: string; nameUr?: string | null }, typed: string): boolean {
  const en = skuNameKey(names.nameEn);
  const ur = skuNameKey(names.nameUr ?? '');
  return typed
    .split(/\s+/)
    .map(skuNameKey)
    .filter(Boolean)
    .every((term) => en.includes(term) || ur.includes(term));
}

/**
 * What to ask the server for a name search. It matches the text as one
 * piece, so a name typed with other spacing or word order would find
 * nothing; asking for its longest word, and checking the rest here with
 * `skuNameMatches`, finds it however it was typed.
 */
export function nameSearchTerm(typed: string): string {
  const words = typed.normalize('NFKC').match(/\p{L}+|\p{N}+/gu) ?? [];
  return words.reduce((longest, word) => (word.length > longest.length ? word : longest), '');
}

/** Natural order, ignoring case: SKU-2 before SKU-10, "feed" beside "Feed". */
export function naturalCompare(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * How closely a code answers what was typed in the SKU box: the code itself
 * first, then codes that start with it, then the rest.
 */
export function skuCodeRank(code: string, typed: string): number {
  if (sameSkuCode(code, typed)) return 0;
  return squash(code).startsWith(squash(typed)) ? 1 : 2;
}

/** The same for a name in the Product name box. */
export function skuNameRank(name: string, typed: string): number {
  if (sameSkuName(name, typed)) return 0;
  return skuNameKey(name).startsWith(skuNameKey(typed)) ? 1 : 2;
}
